// The styled dropdown's pure half — 05j-2. main.js's findStyledSelectLabel /
// filterStyledSelectItems, copied exactly. The component is StyledSelect.tsx.

import type { ReactNode } from "react";
import { LIVE } from "../live/flag.js";
import { fuzzyMatches, withCountryAliases } from "../data/fuzzy-search.js";

export interface StyledSelectItem {
  value: string;
  label: string;
  /** Live: a small picture before the label (a league's flag). */
  icon?: ReactNode;
  /** What the closed button shows when it differs from the list's label (a league keeps its
   *  "(England)" on the button, where there's no flag or heading). */
  buttonLabel?: string;
}

/** A labelled block of options (an <optgroup> equivalent) — e.g. Ranking's metrics by
 *  subcategory. One level deep. */
export interface StyledSelectGroup {
  group: string;
  items: StyledSelectItem[];
}

// A flat list, a grouped list, or a MIX (Comparison's Size / Colour: "None" / "Position",
// then the metric groups) — the dropdown branches per entry.
export type StyledSelectEntry = StyledSelectItem | StyledSelectGroup;

// main.js tested `it.group` for truth, so a group needs a non-empty name.
export const isGroup = (it: StyledSelectEntry): it is StyledSelectGroup => !!(it as Partial<StyledSelectGroup>).group;

/** The label of the option with this value, searching groups too; null if none has it.
 *  null (not "") is the "not found" answer, so a value outside the FIRST group is still
 *  found (main.js's own fix for a blank Comparison axis). */
export function findStyledSelectLabel(items: readonly StyledSelectEntry[], value: string): string | null {
  for (const it of items) {
    if (isGroup(it)) {
      const found = findStyledSelectLabel(it.items, value);
      if (found !== null) return found;
    } else if (it.value === value) {
      return it.buttonLabel ?? it.label;
    }
  }
  return null;
}

/** The options whose label contains the query (trimmed, any case); a group stays only
 *  while it still has a match. An empty query keeps the list as it is. */
export function filterStyledSelectItems(items: readonly StyledSelectEntry[], query: string): readonly StyledSelectEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const out: StyledSelectEntry[] = [];
  // Live: forgiving (accents, word order, one typo — fuzzy-search.ts); the demo keeps its plain match.
  // Live: other names for a country count too ("Holland" → the Netherlands heading).
  const hit = (label: string) => (LIVE ? fuzzyMatches(query, withCountryAliases(label)) : label.toLowerCase().includes(q));
  items.forEach((it) => {
    if (isGroup(it)) {
      // Live: a heading that matches keeps its whole group ("Spain" lists every Spanish league).
      if (LIVE && hit(it.group)) {
        out.push(it);
        return;
      }
      const matched = it.items.filter((child) => hit(child.label));
      if (matched.length) out.push({ group: it.group, items: matched });
    } else if (hit(it.label)) {
      out.push(it);
    }
  });
  return out;
}
